import {Platform, Pressable, ScrollView, StyleSheet, Text, View} from "react-native";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "../../theme/colors";
import { Button } from "../../components";
import { ButtonType, Frame, IsMobile } from "../../theme";
import { useNavigation } from "@react-navigation/native";
import CurvedBgImage from "../../components/CurvedBgImage";
import Checkbox from "../../components/Checkbox";
import AsyncStorage from "@react-native-async-storage/async-storage";
import useAuth from "../../contexts/Auth/useAuth";
import { WellBeingRoute } from "../../navigation/AppStack/routes";

// Icons
import ChatIcon from "../../Icons/ChatIcon";
import ProfileIcon from "../../Icons/ProfileIcon";
import WB from "../../Icons/WB";
import WalletIcon from "../../Icons/WalletIcon";
import {WEB_MAXWIDTH} from "../../../constants";
import {styles} from "../../theme/styles";

const WelcomeScreen = () => {
    const [hideWelcome, setHideWelcome] = useState(false);
    const theme = useTheme();
    const navigation = useNavigation();
    const { user } = useAuth();

    const welcomeScreenStyles = useMemo(() => getWelcomeScreenStyles(theme), [theme]);

    useEffect(() => {
        const onWelcomeScreenLoad = async () => {
            const hideWelcomeCheckValue = (await AsyncStorage.getItem("hideWelcome")) ?? "";
            if (hideWelcomeCheckValue === "true") {
                navigation.navigate(WellBeingRoute);
            }
        };
        onWelcomeScreenLoad();
    }, [navigation]);

    const onClickDontShowCheck = useCallback(async (event) => {
        await AsyncStorage.setItem("hideWelcome", event.checked.toString());
        setHideWelcome(event.checked);
    }, []);

    const onPressChatButton = useCallback(() => navigation.navigate("Chat"), [navigation]);
    const onPressWellbeingButton = useCallback(() => navigation.navigate(WellBeingRoute), [navigation]);
    const onPressVaultButton = useCallback(() => navigation.navigate("WalletStackRoute", { screen: "WalletScreenRoute" }), [navigation]);
    const onPressProfileButton = useCallback(() => navigation.navigate("ProfileStackRoute", { screen: "ProfileScreenRoute" }), [navigation]);

    return (

      <ScrollView
        style={[welcomeScreenStyles.scrollView, { backgroundColor: theme.BACKGROUND }]}
        contentContainerStyle={[
            welcomeScreenStyles.scrollViewContent,
            { alignItems: "center" },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
          <View style={styles.webContainer}>
              <CurvedBgImage />
              <View style={welcomeScreenStyles.header}>
                  <Text testID="welcome-heading" style={welcomeScreenStyles.heading}>Welcome To OneSpace!</Text>
              </View>
          </View>
          <View style={welcomeScreenStyles.viewWrapper}>
              <Text style={welcomeScreenStyles.title}>How does it work?</Text>

              <ShareWrapper onPress={onPressChatButton} Icon={ChatIcon} title="Chat with Personal Assistant"
                            description="Guides you through the passport, helping you fill it out. Receive nudges and connect with external services."/>
              <ShareWrapper onPress={onPressWellbeingButton} Icon={WB} title="Access Your Passport"
                            description="Gather information about yourself in one place."/>
              <ShareWrapper onPress={onPressVaultButton} Icon={WalletIcon} title="Store & Share From the Vault"
                            description="Store your important documents for easy access and share them with a few clicks."/>
              <ShareWrapper onPress={onPressProfileButton} Icon={ProfileIcon} title="View and Edit Your Profile"
                            description="It does what it says on the tin. Personalize your OneSpace."/>

              <View style={welcomeScreenStyles.center}>
                  <Button title="Let's Get Started" variant={ButtonType.secondary} onPress={onPressChatButton} />
              </View>

              <Checkbox styles={welcomeScreenStyles.checkbox} name="hide_welcome" text="Don't show again"
                        checked={hideWelcome} onPressCheckbox={onClickDontShowCheck} />
          </View>
      </ScrollView>
    );
};

const ShareWrapper = ({ onPress, Icon, title, description }) => {
    const theme = useTheme();
    const styles = useMemo(() => getShareWrapperStyles(theme), [theme]);

    return (
      <View style={styles.shareWrapper}>
          <Pressable onPress={onPress}>
              <Icon width={40} height={40} />
          </Pressable>
          <View style={styles.shareWrapperTextContainer}>
              <Text style={styles.subTitle}>{title}</Text>
              <Text style={styles.description}>{description}</Text>
          </View>
      </View>
    );
};

export default WelcomeScreen;

const getWelcomeScreenStyles = (theme) => StyleSheet.create({
    scrollView: {
        flex: 1,
        backgroundColor: theme.BACKGROUND,
    },
    scrollViewContent: {
        paddingBottom: 60,
        alignItems: "center",
    },
    header: {
        height: 95,
        justifyContent: "center",
        alignItems: "center",
        borderBottomStartRadius: 50,
        borderBottomEndRadius: 50,
        width: "100%",
        backgroundColor: "transparent", // Changed from theme.BACKGROUND
        zIndex: 2, // Keep the text on top of the image
    },
    heading: {
        fontSize: 20,
        fontWeight: "600",
        color: theme.TEXT,
        textAlign: "center",
    },
    contentWrapper: {
        width: "100%",
        maxWidth: IsMobile ?  Frame.width : WEB_MAXWIDTH,
        alignSelf: "center",
    },
    title: {
        fontSize: 24,
        fontWeight: "600",
        textAlign: "center",
        marginTop: 30,
        marginBottom: 40,
    },
    center: {
        justifyContent: "center",
        alignItems: "center",
        marginTop: 30,
    },
    checkbox: {
        flexDirection: "row-reverse",
        alignSelf: "center",
        marginTop: 10,
    },
    viewWrapper: {
        width: "100%",
        maxWidth: IsMobile ? Frame.width : WEB_MAXWIDTH,
        alignSelf: "center",
        paddingHorizontal: 20,
        //padding: IsMobile ?  0 : 50
    }

});

const getShareWrapperStyles = (theme) => StyleSheet.create({
    shareWrapper: {
        flexDirection: "row",
        alignItems: "flex-start",
        paddingVertical: 15,
        gap: 20,
    },
    shareWrapperTextContainer: {
        flex: 1,
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 10,
    },
    subTitle: {
        fontSize: 16,
        fontWeight: "600",
    },
    description: {
        flexShrink: 1,
    }
});